import React from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import { ReactComponent as TickWithBorderIcon } from "../../../assets/images/icons/tick-with-border.svg";
import { formatCurrency } from "../../../utils/utils";

const MiniInvoiceWidget = ({
  restaurantName,
  orderData,
  primaryBgColor = null,
  primaryFontColor = null,
  secondaryBgColor = null,
  secondaryFontColor = null,
  tickIconBgColor = null,
}) => {
  // Determine payment status and styling
  const getPaymentStatus = () => {
    const status = orderData?.paymentStatus || orderData?.transactionStatus || orderData?.status;
    
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'success':
        return {
          text: 'PAID',
          color: '#28a745',
          textColor: '#fff'
        };
      case 'pending':
      case 'processing':
        return {
          text: 'PENDING',
          color: '#ffc107',
          textColor: '#000'
        };
      case 'failed':
      case 'cancelled':
      case 'declined':
        return {
          text: 'FAILED',
          color: '#dc3545',
          textColor: '#fff'
        };
      default:
        return {
          text: 'PAID',
          color: '#28a745',
          textColor: '#fff'
        };
    }
  };

  const paymentStatus = getPaymentStatus();

  return (
    <Box
      sx={{
        ...styles.miniInvoiceWidget,
        backgroundColor: primaryBgColor ?? "#95E7BB",
      }}
    >
      <Box
        sx={{
          ...styles.topView,
          "#tick-circle-path": {
            fill: primaryBgColor ?? "#95E7BB",
            stroke: tickIconBgColor ?? "#FFF",
          },
          "#tick-path": { fill: tickIconBgColor ?? "#FFF" },
        }}
      >
        <TickWithBorderIcon style={styles.tickIcon} />
        
        {/* Payment Status Box */}
        <Box
          sx={{
            position: "absolute",
            top: "15px",
            right: "15px",
            backgroundColor: paymentStatus.color,
            color: paymentStatus.textColor,
            padding: "6px 12px",
            borderRadius: "6px",
            fontSize: "12px",
            fontFamily: "Inter",
            fontWeight: 700,
            letterSpacing: "1px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          {paymentStatus.text}
        </Box>

        <Typography sx={{ 
          mt: "2rem", 
          fontSize: "1.25rem", 
          color: primaryFontColor ?? "#212529",
          fontFamily: "Inter",
          fontWeight: 600,
          textAlign: "center",
        }}>
          {restaurantName}
        </Typography>
        <Typography
          sx={{ ...styles.thankyouText, color: primaryFontColor ?? "#212529" }}
        >
          Thank you for your order!
        </Typography>
        <Typography
          sx={{
            ...styles.orderPlacedText,
            color: primaryFontColor ?? "#212529",
          }}
        >
          Your order #
          {orderData?.orderRef ?? orderData?._id.toString().substr(-5)} has been
          placed.&lrm;
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          gap: 2,
          mt: 2,
        }}
      >
        {/* <Box>
					<Typography sx={styles.defListLabel}>10 min</Typography>
					<Typography sx={styles.defListValue}>Est. Delivery</Typography>
				</Box> */}
        <Box>
          <Typography
            sx={{
              ...styles.defListLabel,
              color: primaryFontColor ?? "#212529",
            }}
          >
            x{orderData?.cart?.length}
          </Typography>
          <Typography
            sx={{
              ...styles.defListValue,
              color: primaryFontColor ?? "#212529",
            }}
          >
            Items ordered
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              ...styles.defListLabel,
              color: primaryFontColor ?? "#212529",
              fontSize: 24,
            }}
          >
            {orderData?.tableNumber}
          </Typography>
          <Typography
            sx={{
              ...styles.defListValue,
              color: primaryFontColor ?? "#212529",
              fontSize: 14,
            }}
          >
            Table No
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              ...styles.defListLabel,
              color: primaryFontColor ?? "#212529",
            }}
          >
            {formatCurrency(orderData?.remainingToPay || orderData?.totalAmount)}
          </Typography>
          <Typography
            sx={{
              ...styles.defListValue,
              color: primaryFontColor ?? "#212529",
            }}
          >
            Total
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          height: "43px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: secondaryBgColor ?? "#47b37b",
          borderBottomRightRadius: "15px",
          borderBottomLeftRadius: "15px",
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Typography
          sx={{ ...styles.timeText, color: secondaryFontColor ?? "#000" }}
        >
          Order placed at:{" "}
          {moment(orderData?.createdAt).format("hh:mm A   MMM D")}
        </Typography>
      </Box>
    </Box>
  );
};

export default MiniInvoiceWidget;

const styles = {
  miniInvoiceWidget: {
    borderRadius: "15px",
    mx: 1,
    mt: 6,
    height: "270px",
    position: "relative",
  },
  topView: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  tickIcon: {
    position: "absolute",
    top: "-30px",
  },
  thankyouText: {
    fontSize: 24,
    fontFamily: "Inter",
    fontWeight: 700,
    textAlign: "center",
    margin: "10px 0 8px 0",
    padding: 0,
  },
  orderPlacedText: {
    fontSize: 16,
    fontFamily: "Inter",
    fontWeight: 400,
    textAlign: "center",
    margin: 0,
    padding: 0,
  },
  defListLabel: {
    fontFamily: "Inter",
    fontSize: 20,
    fontWeight: 700,
    textAlign: "center",
  },
  defListValue: {
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: 400,
    opacity: 1,
    textAlign: "center",
  },
  sendEmailText: {
    color: "#212529",
    fontSize: 14,
    fontFamily: "GilroySemiBold",
    textAlign: "center",
  },
  timeText: {
    fontSize: 14,
    fontFamily: "Inter",
    fontWeight: 400,
    textAlign: "center",
  },
};
